/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { DATE_FORMAT } from '@/constants';

// Module autonome : propre `dayjs.extend`, ne pas dépendre d'un ordre d'import
// fortuit (même piège que `validationInformationsNaissance`/`formatters`).
dayjs.extend(customParseFormat);

/**
 * Valide la section « INFORMATIONS SUR L'ÉVÉNEMENT » d'un acte de décès.
 *
 * Obligatoires : date de décès, lieu de décès (commune). Cohérence
 * heure/minute. La date doit être valide, non future, et antérieure ou égale à
 * la date de l'acte — un acte ne peut pas être dressé avant le décès qu'il
 * constate.
 *
 * ⚠️ Pas de contrôle croisé par sous-type, contrairement à la reconnaissance :
 * les cinq sous-types de décès (`D`, `T`, `ESV`, `JDD`, `JDA`) ne portent
 * aucune règle de chronologie propre. Une transcription ou un jugement
 * déclaratif est justement dressé longtemps après le décès, ce que la règle
 * « décès avant l'acte » couvre déjà.
 *
 * Les clés correspondent aux `name` des champs d'`InformationsEvenementDeces`.
 */
const validationInformationsEvenementDeces = (data: Record<string, any>) => {
  const errors = {
    dateDeces: '',
    heureDeces: '',
    minuteDeces: '',
    adresseDeces: '',
  };

  const dateDeces = dayjs(data?.dateDeces, DATE_FORMAT, true);

  if (_.isEmpty(data?.dateDeces)) {
    errors.dateDeces = 'La date de décès est obligatoire';
  } else if (!dateDeces.isValid()) {
    errors.dateDeces = 'La date de décès est invalide';
  } else if (dateDeces.isAfter(dayjs(), 'day')) {
    errors.dateDeces =
      "La date de décès ne peut pas être postérieure à aujourd'hui";
  } else if (!_.isEmpty(data?.dateActe)) {
    const dateActe = dayjs(data.dateActe, DATE_FORMAT, true);
    if (dateActe.isValid() && dateDeces.isAfter(dateActe, 'day')) {
      errors.dateDeces =
        "La date de décès doit être antérieure ou égale à la date de l'acte";
    }
  }

  if (!_.isNil(data?.heureDeces) && _.isNil(data?.minuteDeces)) {
    errors.minuteDeces = 'Les minutes sont obligatoires';
  }

  if (!_.isNil(data?.minuteDeces) && _.isNil(data?.heureDeces)) {
    errors.heureDeces = "L'heure est obligatoire";
  }

  if (_.isEmpty(data?.adresseDeces?.libelleCommune)) {
    errors.adresseDeces = 'Le lieu de décès est obligatoire';
  }

  return _.pickBy(errors, (value) => value !== '');
};

export default validationInformationsEvenementDeces;
